import type { IBotNetworkInterface, IBotNetworkSettingsHTTP } from '../types/bot_interface';
import type { IBotNetwork, IToBotMessagePacket, IFeedback, t_network_types, t_socket_state } from '../types/network';

import type { IControllerIO, ResponseAPI } from '@eab-pip/pi-io';
import f1 from '@eab-pip/pi-f1';
import io from '@eab-pip/pi-io';

import { MessagingFacade } from '../facades/messaging_facade';

/**
 * make a http call to a bot service and accept the response
 * @note we may never use this because if we only use web sockets then this is useless
 */
export class NetworkHTTP implements IBotNetwork {
  /**
   * io to communicate with network
   * @todo we should have access to this to add interceptors or include
   */
  readonly ioHTTP = io.create('api').include('api/hosting');

  /**
   * if the network already in streaming of a message, we need to disable the send button
   */
  __inStreaming = false;

  /**
   * store the last controller so if we shutdown then we stop the last message
   * @todo implement as an array
   */
  controller: IControllerIO;

  constructor(public stateInterface: IBotNetworkInterface, public messagingFacade: MessagingFacade) {}

  /**
   * if the uri begins with `http` then it is a http connection
   */
  static isOf(uri: string): boolean {
    return uri.match(/^http/) != null;
  }

  public get inStreaming(): boolean {
    return this.__inStreaming;
  }

  async startup() {
    return this;
  }

  /**
   * abort the last call if closed prematurely
   */
  async shutdown() {
    if (this.controller) {
      this.controller.abort();
    }

    return this;
  }

  /**
   * send a message body and receive the response
   */
  send(packet: IToBotMessagePacket) {
    const stateInterface = this.stateInterface;
    const messagingFacade = this.messagingFacade;
    const uriSettings = f1.read('/uriSettings', {} as IBotNetworkSettingsHTTP, stateInterface);
    const settings = f1.merge(
      'over',
      {
        send: {
          headers: {},
          method: 'POST',
          beforeRequest(requestPacket: object, packet: object) {
            return requestPacket;
          },
          afterResponse(msg: ResponseAPI) {
            const packet = msg ?? {};
            const more = f1.read('/data/more', false, packet);
            const token = f1.read('/data/token', '', packet);
            const text = f1.read('/data/text', '', packet);

            messagingFacade.writeMessageFromBot(text);

            if (f1.is.false(more)) {
              messagingFacade.notifyStreamingDone(token);
            }
          }
        }
      },
      uriSettings
    );

    // NOTE: f1.merge has a defect in 24.10 which does not permit merging of functions
    // in 24.11 we can remove this and allow the keys to be merged with `f1.merge`
    //
    const beforeRequest = uriSettings?.send?.beforeRequest ?? settings.send.beforeRequest;
    const afterResponse = uriSettings?.send?.afterResponse ?? settings.send.afterResponse;

    const uri = stateInterface.uriFn ? stateInterface.uriFn() : `${stateInterface.uri || ''}`;
    const controller = (this.controller = this.ioHTTP.controller());
    const body = JSON.stringify(packet.messages_formatted);
    const method = settings.send.method;
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...settings.send.headers
    };

    const requestPacket = beforeRequest(
      {
        controller,
        headers,
        method,
        body
      },
      packet
    );

    return this.ioHTTP
      .invoke(uri, requestPacket)
      .then((msg) => {
        if (!msg.response.ok) {
          this.messagingFacade.writeError(this.messagingFacade.errors.CONN_FAIL(), 'throw-error');
        }
        // on an error we should not parse the data
        //
        else {
          afterResponse(msg);
        }

        return msg;
      })
      .finally(() => (this.__inStreaming = false))
      .then(() => packet);
  }

  /**
   * send a feedback to the bot for HTTP network connection
   */
  sendFeedback(feedback: IFeedback) {
    const uriSettings = f1.read('/uriSettings', {} as IBotNetworkSettingsHTTP, this.stateInterface);
    const settings = f1.merge(
      'over',
      {
        feedback: {
          beforeRequest(requestPacket: object, feedback: IFeedback) {
            return requestPacket;
          },
          afterResponse(msg: ResponseAPI) {
            return msg;
          },
          uri: '/sendFeedback',
          method: 'POST',
          headers: {}
        }
      },
      uriSettings
    );

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...settings.feedback.headers
    };

    // NOTE: f1.merge has a defect in 24.10 which does not permit merging of functions
    // in 24.11 we can remove this and allow the keys to be merged with `f1.merge`
    //
    const beforeRequest = uriSettings?.feedback?.beforeRequest ?? settings.feedback.beforeRequest;
    const afterResponse = uriSettings?.feedback?.afterResponse ?? settings.feedback.afterResponse;

    const requestPacket = beforeRequest(
      {
        method: settings.feedback.method,
        body: feedback as unknown as BodyInit,
        headers
      },
      feedback
    );

    return this.ioHTTP.invoke(settings.feedback.uri, requestPacket).then((msg) => {
      if (!msg.response.ok) {
        this.messagingFacade.writeError(this.messagingFacade.errors.CONN_FAIL(), 'throw-error');
      }
      // on an error we should not parse the data
      //
      else {
        afterResponse(msg);
      }

      return msg;
    });
  }

  /**
   * To know if the connection is of http
   */
  ofType(type: t_network_types): type is 'http' {
    return type === 'http';
  }

  /**
   * @returns connection state
   */
  getState(): t_socket_state {
    return 'open';
  }
}
